import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { useApiQuery } from '@/hooks/use-api'
import { i18n } from '@/lib/i18n'

const BASE = '/zalo-friend-campaigns'
const KEY = ['zalo-friend-campaigns']

export type TrangThaiChienDich = 'draft' | 'running' | 'paused' | 'completed' | 'cancelled'
export type TrangThaiTaiKhoan = 'active' | 'paused' | 'done' | 'blocked'
export type TrangThaiKhach =
  | 'pending' | 'invited' | 'accepted' | 'declined' | 'skipped' | 'failed'

export interface TaiKhoanChienDich {
  id: string
  channelAccountId: string
  status: TrangThaiTaiKhoan
  stoppedReason: string | null
  startedAt: string | null
  lastInviteAt: string | null
  nextInviteAt: string | null
  invitedCount: number
  acceptedCount: number
  channelAccount?: { id: string; displayName: string | null; status: string }
}

export interface ChienDichKetBan {
  id: string
  name: string
  source: 'csv' | 'segment'
  status: TrangThaiChienDich
  inviteMessage: string
  welcomeMessages: string[]
  autoWelcome: boolean
  dailyLimit: number
  warmupEnabled: boolean
  sendWindowStart: number
  sendWindowEnd: number
  totalCount: number
  invitedCount: number
  acceptedCount: number
  failedCount: number
  skippedCount: number
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  accounts: TaiKhoanChienDich[]
}

export interface ChiTietChienDich extends ChienDichKetBan {
  soBanTheoTaiKhoan: { channelAccountId: string; soLienHe: number }[]
}

export interface KhachChienDich {
  id: string
  channelAccountId: string
  phone: string
  displayName: string
  status: TrangThaiKhach
  orderIndex: number
  errorMessage: string | null
  invitedAt: string | null
  acceptedAt: string | null
  welcomeSentCount: number
  overflowedAt: string | null
}

/** Tỷ lệ chấp nhận — chỉ số cần theo dõi sát nhất từ tuần đầu. */
export function tyLeChapNhan(c: Pick<ChienDichKetBan, 'invitedCount' | 'acceptedCount'>): number {
  return c.invitedCount > 0 ? c.acceptedCount / c.invitedCount : 0
}

export const NHAN_TRANG_THAI: Record<TrangThaiChienDich, string> = {
  get draft() { return i18n.t('marketing:friendCampaignStatus.draft') },
  get running() { return i18n.t('marketing:friendCampaignStatus.running') },
  get paused() { return i18n.t('marketing:friendCampaignStatus.paused') },
  get completed() { return i18n.t('marketing:friendCampaignStatus.completed') },
  get cancelled() { return i18n.t('marketing:friendCampaignStatus.cancelled') },
}

export function useChienDichKetBan() {
  return useApiQuery<ChienDichKetBan[]>(KEY, BASE, undefined, {
    placeholderData: (prev) => prev,
  })
}

/**
 * Chiến dịch chạy ngầm hàng tháng — người vận hành cần thấy số nhảy mà không
 * phải F5, nên tự refetch khi đang chạy.
 */
export function useChiTietChienDich(id: string | undefined) {
  return useQuery<ChiTietChienDich>({
    queryKey: ['zalo-friend-campaign', id],
    queryFn: async () => (await api.get<ChiTietChienDich>(`${BASE}/${id}`)).data,
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 30_000 : false),
  })
}

export function useKhachChienDich(
  id: string | undefined,
  params?: { status?: string; skip?: number; take?: number },
) {
  return useApiQuery<{ rows: KhachChienDich[]; tong: number }>(
    ['zalo-friend-campaign-targets', id, params ?? {}],
    `${BASE}/${id}/targets`,
    params as Record<string, unknown> | undefined,
    { enabled: !!id, placeholderData: (prev) => prev },
  )
}

/** Khách vượt trần tin tự động — nhân viên phải chăm tay. */
export function useKhachCanChamTay(id: string | undefined) {
  return useApiQuery<KhachChienDich[]>(
    ['zalo-friend-campaign-overflow', id],
    `${BASE}/${id}/overflow`,
    undefined,
    { enabled: !!id },
  )
}

function useDungMutation<TInput>(goi: (input: TInput) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: goi,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['zalo-friend-campaign'] })
    },
  })
}

export function useTaoChienDich() {
  return useDungMutation<Partial<ChienDichKetBan>>(async (input) =>
    (await api.post<ChienDichKetBan>(BASE, input)).data,
  )
}

export function useSuaChienDich(id: string) {
  return useDungMutation<Partial<ChienDichKetBan>>(async (input) =>
    (await api.patch<ChienDichKetBan>(`${BASE}/${id}`, input)).data,
  )
}

export function useNapTep(id: string) {
  return useDungMutation<{
    rows: { phone: string; ten?: string; tai_khoan?: string; thu_tu?: number }[]
    accountIds: string[]
  }>(async (input) =>
    (await api.post<{ them: number; boQua: number; tong: number }>(
      `${BASE}/${id}/targets/import`, input,
    )).data,
  )
}

/** start | pause | cancel ở cấp chiến dịch. */
export function useDieuKhienChienDich(id: string) {
  return useDungMutation<'start' | 'pause' | 'cancel'>(async (hanhDong) =>
    (await api.post(`${BASE}/${id}/${hanhDong}`)).data,
  )
}

/** pause | resume ở cấp TỪNG tài khoản — một tài khoản bị khoá không phải dừng cả chiến dịch. */
export function useDieuKhienTaiKhoan(id: string) {
  return useDungMutation<{ accountId: string; hanhDong: 'pause' | 'resume' }>(
    async ({ accountId, hanhDong }) =>
      (await api.post(`${BASE}/${id}/accounts/${accountId}/${hanhDong}`)).data,
  )
}
